import { useState } from 'react';
import { Link } from 'react-router';
import { Monitor } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@staff-app/ui';
import { CustomerSearchBox } from './CustomerSearchBox';
import { useStaffSearchDevices } from '../hooks/useStaffCustomer';

function devicesLink(accountId: string, uuid?: string): string {
  const base = `/staff/customers/${encodeURIComponent(accountId)}/devices`;
  if (!uuid) return base;
  const params = new URLSearchParams({ deviceUUID: uuid });
  return `${base}?${params.toString()}`;
}

/**
 * Devices column — search by device name (Atlas Search `deviceSearch`) or by
 * full UUID (exact `UUID_1` lookup). Backend merges both branches.
 */
export function DevicesSearchColumn() {
  const [query, setQuery] = useState('');
  const { data, loading, error } = useStaffSearchDevices(query);
  const results = data?.staffSearchDevices ?? [];

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border bg-card p-4"
      data-testid="devices-search-column"
    >
      <header className="flex items-center gap-2">
        <Monitor className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">{'Devices'}</h2>
      </header>
      <CustomerSearchBox
        initialValue={query}
        onSubmit={setQuery}
        placeholder={'Device name or UUID'}
        ariaLabel={'Search devices'}
        testId="devices-search-box"
      />
      {error ? (
        <p className="text-xs text-destructive" data-testid="devices-search-error">
          {'Search failed.'} {error.message}
        </p>
      ) : loading ? (
        <p className="py-4 text-center text-xs text-muted-foreground">{'Searching…'}</p>
      ) : query.trim().length < 2 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">{'Type to search…'}</p>
      ) : results.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">{'No results.'}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{'Device'}</TableHead>
              <TableHead>{'Account'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {results.map((r) => (
              <TableRow
                key={r.deviceId}
                className="hover:bg-accent/50 transition-colors"
                data-testid="devices-search-result-row"
              >
                <TableCell className="font-medium">
                  <Link to={devicesLink(r.accountId, r.UUID)} className="hover:underline">
                    {r.deviceName ?? '—'}
                  </Link>
                  <div className="font-mono text-[10px] text-muted-foreground">
                    {r.UUID ?? r.deviceId}
                  </div>
                </TableCell>
                <TableCell className="text-xs">
                  <div>{r.companyName ?? '—'}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{r.accountId}</div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}
